import { CheckCircle2 } from 'lucide-react';
import { InfoDot } from '../hiring/HiringForm';
import { ByWhenField } from './ByWhenField';
import { CardLook } from './CardLook';
import { CopyFields } from './CopyFields';
import { FillFormFields } from './FillFormFields';
import { ReachLevelFields } from './ReachLevelFields';
import { SendToField } from './SendToField';
import { ConsentFields, ReadThisFields } from './TaskButtonFields';
import { ChoiceCard, FieldLabel } from './bannerFormBits';
import type { BannerComposer } from './useBannerComposer';

// Two separate switches do two separate things — say so, or an admin sends this expecting
// it to light up the dots.
const RESUME_NOTE =
  "People who already have a resume never see it. Red dots in the app's Document Vault are separate: they appear only on projects where Access Management → Mobile Access → HR → Resume Reminder is ticked.";

/** A to-do pinned to people's home page until it is done. */
export function TaskForm({ composer }: { composer: BannerComposer }) {
  const { audience, level, form, buttons, taskType, taskDef, taskTypes, release, doneWhen, selectTaskType, taskSendPush, setTaskSendPush } = composer;
  const { selectedProjectId, details } = audience;

  return (
    <>
      {/* A registry rather than a one-off, so the next task is an entry in TASK_TYPES,
          not a new form. */}
      <div>
        <FieldLabel text="What must they do?" />
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
          {taskTypes.map((t) => (
            <ChoiceCard
              key={t.value}
              label={t.label}
              line={t.cardLine}
              tag={t.tag}
              title={t.description}
              on={taskType === t.value}
              onClick={() => selectTaskType(t)}
            />
          ))}
        </div>
      </div>

      <div className="mt-3.5 grid gap-x-8 gap-y-4 xl:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-3.5">
          {taskType === 'read_this' && buttons.readThisButton && <ReadThisFields buttons={buttons} />}
          {taskType === 'read_this' && !buttons.readThisButton && !release.loading && (
            <p className="flex items-center gap-1 text-sm text-gray-700">
              <span>A plain banner: no button yet.</span>
              <InfoDot text={release.reason} />
            </p>
          )}
          {taskType === 'consent' && <ConsentFields buttons={buttons} />}
          {taskType === 'fill_form' && selectedProjectId != null && <FillFormFields form={form} />}
          {taskType === 'reach_level' && selectedProjectId != null && <ReachLevelFields level={level} details={details} audienceCounting={audience.matchLoading} />}

          {doneWhen && (
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <CheckCircle2 className="h-4 w-4 flex-none text-green-700" />
              <span>
                <span className="font-medium text-gray-900">Done when</span> {doneWhen}
              </span>
              <InfoDot text={taskType === 'resume_upload' ? `${taskDef.description} ${RESUME_NOTE}` : taskDef.description} />
            </div>
          )}

          <ByWhenField composer={composer} />

          <div className="flex items-center gap-1">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={taskSendPush}
                onChange={(e) => setTaskSendPush(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 accent-primary-600 focus:ring-primary-500"
              />
              Also send a push
            </label>
            <InfoDot text={taskDef.pushHint} />
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-3.5">
          <SendToField audience={audience} counted={level.preview?.total ?? null} />
          {/* The rules take the room of a line of message, so the form still fits. */}
          <CopyFields composer={composer} rows={audience.recipientMode === 'details' ? 2 : 3} />
          <CardLook composer={composer} />
        </div>
      </div>
    </>
  );
}
